/* ============================================================
   Malcriados — Cart drawer logic (GLOBAL)
   Carga en layout/theme.liquid → corre en todas las páginas.
   - Cache local del cart con properties por line item
   - Decoración del drawer: extras vinculados se inyectan dentro
     del cart-item de su prenda padre + total del pack
   - Interceptor de remove: si elimina prenda con mejoras, se
     eliminan juntos. Si elimina mejora, también la prenda padre.
   - Refresh del drawer desde server cuando cambia el cart
   - Expone window.MalcriadosCart para usar desde otros JS (PDP)
   ============================================================ */

(function () {
  'use strict';

  // ============== Cache del cart ==============
  let cartItemsCache = [];

  async function refreshCartCache() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      cartItemsCache = (cart.items || []).map((it, idx) => {
        const props = it.properties || {};
        const original = it.original_line_price || (it.price * (it.quantity || 1));
        let price = it.final_line_price || (it.price * (it.quantity || 1));

        // Reflejar el descuento upsell en el cache para que el drawer muestre
        // el "was" tachado y el precio final correcto. La Shopify Function
        // aplica el descuento real en checkout vía la line item property
        // `_mc_extra_discount_clp`; acá hacemos el espejo visual.
        //
        // Si Shopify ya reflejó el descuento en `final_line_price` (puede
        // pasar con descuentos parciales), respetamos lo más bajo de ambos.
        const upsellDiscountClp = parseInt(props._mc_extra_discount_clp || '0', 10);
        if (upsellDiscountClp > 0) {
          const discountCents = upsellDiscountClp * 100;
          const expectedAfterDiscount = Math.max(0, original - discountCents);
          price = Math.min(price, expectedAfterDiscount);
        }

        return {
          key: it.key,
          line: idx + 1,
          props,
          title: it.title,
          price,
          original,
          image: (it.featured_image && it.featured_image.url) || it.image || null,
        };
      });
    } catch (e) {
      console.warn('[Malcriados Cart] no se pudo refrescar cache:', e);
    }
  }

  function formatCLPCents(cents) {
    const pesos = Math.round((cents || 0) / 100);
    return '$' + pesos.toLocaleString('es-CL');
  }

  function getCacheItemForLine(lineNum) {
    return cartItemsCache.find((it) => it.line === lineNum);
  }

  // ============== Lógica de vinculación ==============
  // Vinculación = prenda ↔ sus mejoras (NO entre prendas del mismo pack).
  function getLinkedKeysForRemove(item) {
    const props = item.props || {};
    const packId = props.__mc_pack_id;
    if (!packId) return [item.key];

    if (props.__mc_pack_pos && !props.__mc_extra_for_pos) {
      const myPos = props.__mc_pack_pos;
      return cartItemsCache
        .filter((it) => {
          if (it.props.__mc_pack_id !== packId) return false;
          if (it.key === item.key) return true;
          if (it.props.__mc_extra_for_pos === myPos) return true;
          return false;
        })
        .map((it) => it.key);
    }

    if (props.__mc_extra_for_pos) {
      const targetPos = props.__mc_extra_for_pos;
      return cartItemsCache
        .filter((it) => {
          if (it.props.__mc_pack_id !== packId) return false;
          if (it.props.__mc_pack_pos === targetPos && !it.props.__mc_extra_for_pos) return true;
          if (it.props.__mc_extra_for_pos === targetPos) return true;
          return false;
        })
        .map((it) => it.key);
    }

    return [item.key];
  }

  async function removeLinkedItems(item) {
    const linkedKeys = getLinkedKeysForRemove(item);
    if (!linkedKeys.length) return;

    const updates = {};
    linkedKeys.forEach((k) => { updates[k] = 0; });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      await refreshCartCache();
      await refreshCartDrawerFromServer();
    } catch (e) {
      console.error('[Malcriados Cart] error eliminando items vinculados:', e);
    }
  }

  // ============== Refresh del drawer ==============
  async function refreshCartDrawerFromServer() {
    try {
      const r = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
        credentials: 'same-origin',
      });
      if (!r.ok) return;
      const sections = await r.json();
      const cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl && sections['cart-drawer']) {
        const parsed = new DOMParser()
          .parseFromString(sections['cart-drawer'], 'text/html')
          .querySelector('#CartDrawer');
        if (parsed) cartDrawerEl.innerHTML = parsed.innerHTML;
      }
      updateCartIconBubble(sections);
      const cartDrawerComp = document.querySelector('cart-drawer');
      if (cartDrawerComp && cartItemsCache.length === 0) {
        cartDrawerComp.classList.add('is-empty');
        const inner = cartDrawerComp.querySelector('.drawer__inner');
        if (inner) inner.classList.add('is-empty');
      }
      decorateCartLines();
    } catch (e) {
      console.warn('[Malcriados Cart] no se pudo refrescar drawer:', e);
    }
  }

  function updateCartIconBubble(respSections) {
    if (!respSections || !respSections['cart-icon-bubble']) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    try {
      const newHTML = new DOMParser()
        .parseFromString(respSections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (newHTML) bubble.innerHTML = newHTML.innerHTML;
    } catch (e) { /* silenciar */ }
  }

  // Compactar propiedades de un cart-item en una sola línea separadas por "·"
  // Ej: "Retratos: 1 retrato · Color: Negro · Talla: S"
  function compactPropertiesInOneLine(row) {
    const dl = row.querySelector('.cart-item__details dl');
    if (!dl) return;
    if (dl.dataset.mcCompacted === '1') return;
    const options = Array.from(dl.querySelectorAll('.product-option'))
      .map((opt) => {
        const dt = opt.querySelector('dt');
        const dd = opt.querySelector('dd');
        if (!dt || !dd) return '';
        return '<span class="mc-prop"><b>' + dt.textContent.replace(':', '').trim() + ':</b> ' + dd.textContent.replace(/[,.]?\s*$/, '').trim() + '</span>';
      })
      .filter(Boolean)
      .join('<span class="mc-prop-sep">·</span>');
    if (options) {
      dl.innerHTML = '<div class="mc-cart-props-inline">' + options + '</div>';
      dl.dataset.mcCompacted = '1';
    }
  }

  // ============== Decoración del drawer ==============
  function decorateCartLines() {
    if (!cartItemsCache.length) return;

    document.querySelectorAll('.mc-cart-mejoras-inyect').forEach((el) => el.remove());

    cartItemsCache.forEach((item) => {
      const props = item.props || {};
      const row = document.querySelector('#CartDrawer-Item-' + item.line);
      if (!row) return;
      row.classList.remove('mc-cart-prenda', 'mc-cart-extra-hidden');
      // La tarjeta compacta aplica a TODO producto del carrito, no solo a
      // los del sistema de packs. Antes se exigía __mc_pack_pos, así que
      // un producto simple (los jockeys, por ejemplo) se quedaba con el
      // layout grande de Dawn y desentonaba con el resto.
      if (!props.__mc_extra_for_pos) {
        row.classList.add('mc-cart-prenda');
        // Compactar las propiedades en una línea con separadores "·"
        compactPropertiesInOneLine(row);
      }
      if (props.__mc_extra_for_pos) {
        row.classList.add('mc-cart-extra-hidden');
      }
    });

    const extrasByParent = new Map();
    const extras = cartItemsCache.filter((it) => it.props && it.props.__mc_extra_for_pos);
    extras.forEach((extra) => {
      const props = extra.props;
      const parent = cartItemsCache.find((p) =>
        p.props &&
        p.props.__mc_pack_id === props.__mc_pack_id &&
        p.props.__mc_pack_pos === props.__mc_extra_for_pos &&
        !p.props.__mc_extra_for_pos
      );
      if (!parent) return;
      if (!extrasByParent.has(parent.line)) extrasByParent.set(parent.line, []);
      extrasByParent.get(parent.line).push(extra);
    });

    extrasByParent.forEach((extrasArr, parentLine) => {
      const parentRow = document.querySelector('#CartDrawer-Item-' + parentLine);
      if (!parentRow) return;
      const detailsCell = parentRow.querySelector('.cart-item__details');
      if (!detailsCell) return;

      const parentItem = cartItemsCache.find((p) => p.line === parentLine);
      const parentPrice = (parentItem && parentItem.price) || 0;
      const parentOriginal = (parentItem && parentItem.original) || parentPrice;

      const safeTitleOf = (s) => (s || 'Mejora')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const mejorasItems = extrasArr.map((extra) => {
        const imgUrl = extra.image
          ? extra.image.replace(/(\.(jpg|jpeg|png|webp))/i, '_64x$1')
          : null;
        const imgHTML = imgUrl
          ? '<img class="mc-cart-mejora-img" src="' + imgUrl + '" alt="" loading="lazy">'
          : '<span class="mc-cart-mejora-icon">✨</span>';
        const isFree = !extra.price || extra.price === 0;
        const hasDiscount = extra.original && extra.original > extra.price;
        const wasHTML = hasDiscount
          ? '<s class="mc-cart-mejora-price-was">+' + formatCLPCents(extra.original) + '</s> '
          : '';
        const priceHTML = isFree
          ? wasHTML + '<span class="mc-cart-mejora-tag-free">GRATIS</span>'
          : wasHTML + '<span class="mc-cart-mejora-price-amount">+' + formatCLPCents(extra.price) + '</span>';
        return '<div class="mc-cart-mejora-row" data-mc-extra-key="' + extra.key + '">' +
          imgHTML +
          '<span class="mc-cart-mejora-name">' + safeTitleOf(extra.title) + '</span>' +
          '<span class="mc-cart-mejora-price">' + priceHTML + '</span>' +
          '<button type="button" class="mc-cart-mejora-trash" data-mc-remove-extra="' + extra.key + '" aria-label="Eliminar mejora">' +
            '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5">' +
              '<path d="M3 5h10M6 5V3.5h4V5M5 5l.5 8h5L11 5" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
          '</div>';
      }).join('');

      const extrasTotal = extrasArr.reduce((sum, e) => sum + (e.price || 0), 0);
      const extrasOriginal = extrasArr.reduce((sum, e) => sum + ((e.original || e.price) || 0), 0);
      const packTotal = parentPrice + extrasTotal;
      const packOriginal = parentOriginal + extrasOriginal;
      const packTotalHTML = packOriginal > packTotal
        ? '<s class="mc-cart-pack-total-was">' + formatCLPCents(packOriginal) + '</s> ' +
          '<span class="mc-cart-pack-total-amount">' + formatCLPCents(packTotal) + '</span>'
        : '<span class="mc-cart-pack-total-amount">' + formatCLPCents(packTotal) + '</span>';

      // El label cambia según el tipo de pack:
      //   - preset (pack pre-armado tipo Pet Dad) → "Pack incluye:"
      //   - sin marca o upsell → "Mejoras incluidas:" (polerón base)
      const parentKind = (parentItem && parentItem.props && parentItem.props.__mc_pack_kind) || '';
      const labelText = parentKind === 'preset' ? 'Pack incluye:' : 'Mejoras incluidas:';

      const wrap = document.createElement('div');
      wrap.className = 'mc-cart-mejoras-inyect';
      wrap.innerHTML =
        '<div class="mc-cart-mejora-block">' +
          '<p class="mc-cart-mejora-label">' + labelText + '</p>' +
          mejorasItems +
        '</div>' +
        '<div class="mc-cart-pack-total">' +
          '<span class="mc-cart-pack-total-label">Total</span>' +
          packTotalHTML +
        '</div>';

      // Crear topRow con [imagen + details + price col] (flex nowrap)
      buildPriceColumn(parentRow, parentItem);

      // Insertar el bloque de mejoras DESPUÉS del topRow (como hermano,
      // no dentro del details). Así queda visualmente como segunda fila
      // de la card, full width.
      parentRow.appendChild(wrap);
    });

    // Todo lo que no sea un extra ni tenga extras colgando lleva la fila
    // horizontal (imagen, detalle y precio). Los que SI tienen extras se
    // arman mas arriba, junto con sus mejoras.
    const prendasSinExtras = cartItemsCache.filter((it) =>
      !(it.props && it.props.__mc_extra_for_pos) &&
      !extrasByParent.has(it.line)
    );
    prendasSinExtras.forEach((item) => {
      const row = document.querySelector('#CartDrawer-Item-' + item.line);
      if (!row) return;
      buildPriceColumn(row, item);
    });
  }

  // Agrupa imagen + details + columna precio en un wrapper flex nowrap.
  // Idempotente: en pasadas subsecuentes (post re-render), captura refs
  // a media/details/trash ANTES de eliminar wrappers viejos para no
  // perder esos elementos al hacer oldTop.remove() / oldPriceCol.remove().
  function buildPriceColumn(row, item) {
    if (!row) return;

    // 1. Capturar referencias a los nodos clave (estén donde estén
    //    en este momento — primera pasada en sus tds nativos, o
    //    pasadas subsecuentes dentro de wrappers anteriores).
    const media = row.querySelector('.cart-item__media');
    const details = row.querySelector('.cart-item__details');
    const trashBtn = row.querySelector('cart-remove-button');

    // 2. Eliminar wrappers anteriores. Como ya tenemos las refs
    //    en memoria, los nodos no se pierden.
    const oldTop = row.querySelector(':scope > .mc-cart-prenda-top');
    if (oldTop) oldTop.remove();
    const oldPriceCol = row.querySelector(':scope > .mc-cart-price-col');
    if (oldPriceCol) oldPriceCol.remove();

    // 3. Construir nuevo layout
    const topRow = document.createElement('div');
    topRow.className = 'mc-cart-prenda-top';

    if (media) topRow.appendChild(media);
    if (details) topRow.appendChild(details);

    const priceCol = document.createElement('div');
    priceCol.className = 'mc-cart-price-col';

    const finalPrice = (item && item.price) || 0;
    const origPrice = (item && item.original) || finalPrice;
    if (origPrice > finalPrice) {
      const wasEl = document.createElement('s');
      wasEl.className = 'mc-cart-prenda-price-was';
      wasEl.textContent = formatCLPCents(origPrice);
      priceCol.appendChild(wasEl);
    }
    const priceEl = document.createElement('span');
    priceEl.className = 'mc-cart-prenda-price';
    priceEl.textContent = formatCLPCents(finalPrice);
    priceCol.appendChild(priceEl);

    if (trashBtn) {
      trashBtn.classList.add('mc-cart-trash-corner');
      priceCol.appendChild(trashBtn);
    }

    topRow.appendChild(priceCol);

    // Insertar topRow al inicio del row
    row.insertBefore(topRow, row.firstChild);

    // Marcar como decorado (CSS muestra opacity 1 cuando esta clase
    // está presente — fallback para navegadores sin :has())
    row.classList.add('mc-decorated');
  }

  // Eliminar una sola mejora por su key (sin tocar prenda ni otras mejoras)
  async function removeOneExtra(key) {
    if (!key) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: 0 } }),
      });
      await refreshCartCache();
      await refreshCartDrawerFromServer();
    } catch (e) {
      console.error('[Malcriados Cart] error eliminando mejora:', e);
    }
  }

  // ============== Interceptor de remove ==============
  let __mcRemoveInterceptorBound = false;
  function bindCartRemoveInterceptor() {
    if (__mcRemoveInterceptorBound) return;
    __mcRemoveInterceptorBound = true;

    document.addEventListener('click', (e) => {
      // Click en trash de una mejora individual (botón inyectado por nosotros)
      const extraTrash = e.target.closest('[data-mc-remove-extra]');
      if (extraTrash) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const key = extraTrash.getAttribute('data-mc-remove-extra');
        // Si la mejora pertenece a un pack pre-armado (kind = preset),
        // el pack es indivisible: borrar la mejora borra todo el pack
        // (la prenda principal + el resto de mejoras). En upsells
        // opcionales del poleron base (sin kind), solo borra la mejora.
        const extraItem = cartItemsCache.find(function(it) { return it.key === key; });
        if (extraItem && extraItem.props && extraItem.props.__mc_pack_kind === 'preset') {
          removeLinkedItems(extraItem);
        } else {
          removeOneExtra(key);
        }
        return;
      }

      // Click en trash nativo de Dawn (cart-remove-button)
      const removeEl = e.target.closest('cart-remove-button a, cart-remove-button button, .cart-remove-button a, .cart-remove-button button');
      if (!removeEl) return;
      const cartItem = removeEl.closest('[id^="CartDrawer-Item-"], [id^="CartItem-"]');
      if (!cartItem) return;

      const idMatch = cartItem.id.match(/(?:CartDrawer-Item-|CartItem-)(\d+)/);
      if (!idMatch) return;
      const lineNum = parseInt(idMatch[1], 10);

      const item = getCacheItemForLine(lineNum);
      if (!item || !item.props) return;

      const linkedKeys = getLinkedKeysForRemove(item);
      if (linkedKeys.length <= 1) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      removeLinkedItems(item);
    }, { capture: true });

    document.addEventListener('cart:refresh', () => {
      setTimeout(() => {
        refreshCartCache().then(() => {
          setTimeout(decorateCartLines, 100);
        });
      }, 200);
    });

    document.addEventListener('click', (e) => {
      const icon = e.target.closest('#cart-icon-bubble');
      if (!icon) return;
      setTimeout(() => {
        refreshCartCache().then(() => {
          setTimeout(decorateCartLines, 50);
        });
      }, 100);
    });

    setTimeout(() => {
      refreshCartCache().then(() => decorateCartLines());
    }, 500);
  }

  // ============== Auto re-decorate on cart drawer changes ==============
  // Cuando Dawn re-renderiza el cart drawer (ej: al eliminar un item
  // normal sin mejoras), nuestras clases mc-cart-prenda se pierden.
  // Un MutationObserver detecta cambios y re-aplica el diseño.
  let __mcDecorating = false;
  let __mcFadeTimeoutId = null;

  // Safety: garantizar que el fade-out NUNCA se quede atascado.
  // 2500ms da margen para que: click → Dawn re-render → observer
  // dispara → refreshCartCache (~150ms) → decorate corra → fade-in.
  // Si decorate completa antes (caso normal), clearFadeNow cancela.
  function clearFadeWithTimeout() {
    clearTimeout(__mcFadeTimeoutId);
    __mcFadeTimeoutId = setTimeout(() => {
      document.body.classList.remove('mc-cart-pending-decorate');
      __mcDecorating = false;
    }, 2500);
  }
  function clearFadeNow() {
    clearTimeout(__mcFadeTimeoutId);
    document.body.classList.remove('mc-cart-pending-decorate');
    __mcDecorating = false;
  }

  function bindCartDrawerObserver() {
    // IMPORTANTE: el target debe ser <cart-drawer> (web component que
    // PERSISTE a través de re-renders de Dawn). Si observamos un hijo
    // como #CartDrawer-CartItems, cuando Dawn reemplaza .drawer__inner
    // ese nodo es eliminado y nuestro observer queda desconectado.
    const target = document.querySelector('cart-drawer');
    if (!target) {
      setTimeout(bindCartDrawerObserver, 500);
      return;
    }

    const observer = new MutationObserver((mutations) => {
      if (__mcDecorating) return;
      // Check robusto: detectar cart-items en cualquier nivel del
      // mutation, incluso dentro de wrappers grandes que Dawn
      // reemplaza (.drawer__inner, etc).
      const relevant = mutations.some((m) => {
        const checkNodes = [...m.addedNodes, ...m.removedNodes];
        return checkNodes.some((n) => {
          if (n.nodeType !== 1) return false;
          if (n.id?.startsWith('CartDrawer-Item-')) return true;
          if (n.classList?.contains('cart-item')) return true;
          if (n.querySelector?.('[id^="CartDrawer-Item-"], .cart-item')) return true;
          return false;
        });
      });
      if (!relevant) return;

      __mcDecorating = true;
      clearFadeWithTimeout();

      // PASO 1: Aplicar las clases pack mc-cart-prenda y
      // mc-cart-extra-hidden INMEDIATAMENTE con el cache actual (sin
      // esperar refresh). Esto da el layout flex correcto al instante.
      // Es seguro porque las rows que ya no existen no se encuentran y
      // las nuevas (caso raro post-remove) caen al default sin clase
      // hasta el decorate completo abajo.
      applyPackClassesFromCache();

      // Fade-in inmediato — cliente ve el diseño nuevo aplicado
      requestAnimationFrame(() => {
        document.body.classList.remove('mc-cart-pending-decorate');
      });

      // PASO 2: En background, refresh cache + decorate completo
      // (mueve trash, inyecta mejoras, calcula total). El cliente ya
      // ve el layout flex correcto, los detalles aparecen sin flash.
      (async () => {
        try {
          await refreshCartCache();
          decorateCartLines();
        } catch (e) {
          console.warn('[Malcriados Cart] error en re-decorate:', e);
        } finally {
          clearTimeout(__mcFadeTimeoutId);
          __mcDecorating = false;
        }
      })();
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  // Aplicación rápida de las clases pack basado en cartItemsCache actual
  // (sin esperar refresh). Solo aplica clases CSS, no inyecta mejoras
  // ni mueve el trash. Eso lo hace decorateCartLines completo después.
  function applyPackClassesFromCache() {
    if (!cartItemsCache.length) return;
    cartItemsCache.forEach((item) => {
      const props = item.props || {};
      const row = document.querySelector('#CartDrawer-Item-' + item.line);
      if (!row) return;
      if (!props.__mc_extra_for_pos) {
        row.classList.add('mc-cart-prenda');
      }
      if (props.__mc_extra_for_pos) {
        row.classList.add('mc-cart-extra-hidden');
      }
    });
  }

  // Preventive fade: aplicar mc-cart-pending-decorate INSTANTÁNEAMENTE
  // al click del trash, antes de que Dawn re-renderice. Así nunca se
  // ve el HTML viejo (intermedio entre click y observer dispara).
  // Safety timeout (800ms) en clearFadeWithTimeout garantiza que si
  // algo falla, se quita sola.
  function bindRemoveClickFade() {
    document.addEventListener('click', (e) => {
      const removeEl = e.target.closest(
        'cart-remove-button a, cart-remove-button button, [data-mc-remove-extra]'
      );
      if (!removeEl) return;
      // Solo aplica si el click es DENTRO del cart drawer
      if (!removeEl.closest('cart-drawer')) return;
      document.body.classList.add('mc-cart-pending-decorate');
      clearFadeWithTimeout();
    }, { capture: true });
  }

  // ============== Boot ==============
  function boot() {
    bindCartRemoveInterceptor();
    bindCartDrawerObserver();
    bindRemoveClickFade();
    refreshCartCache();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  // ============== API pública para PDP y otros JS ==============
  window.MalcriadosCart = {
    refreshCache: refreshCartCache,
    decorate: decorateCartLines,
    refreshDrawerFromServer: refreshCartDrawerFromServer,
    updateIconBubble: updateCartIconBubble,
    formatCLPCents: formatCLPCents,
  };
})();
